import {
  Button,
  Icon,
  InformationCard,
  SafeHtmlContent,
  Text
} from '@yes.technology/react-toolkit'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import { useIsOpen } from 'shared/hooks'
import useDeviceType from 'shared/hooks/useDeviceType'
import { ChartTemplate } from '../../Cockpit.types'
import HalfDonutChart, {
  HalfDonutChartProps
} from '../SemiCircleChart/HalfDonutChart'
import {
  Card,
  CardContentGrid,
  CardContentRow,
  CardHeader,
  CardHeaderContent,
  CardResultContent,
  CardSubResultContent,
  ChartWrapper,
  fontSizeMapping,
  Image
} from './styles'
import { formatNumberWithDots } from './utils/formatNumberWithDots'

type ResultGoalCardChartItemProps = {
  contentSize: keyof typeof fontSizeMapping
  title: string
  result: number
  goal: number
  subtitle?: string
  info?: string
  imageUrl?: string
  chartColor?: string
  prefix?: string
  chartTemplate?: Partial<ChartTemplate>
}

const ResultGoalCardChartItem = ({
  contentSize = 'small',
  title,
  result,
  goal,
  subtitle = '',
  info = '',
  imageUrl = '',
  chartColor = '',
  prefix = '',
  chartTemplate = {}
}: ResultGoalCardChartItemProps) => {
  const { isOpen, open, close } = useIsOpen()

  const { t } = useTranslation()

  const deviceType = useDeviceType()

  const { props, resultPercentage } = useMemo(() => {
    const resultPercentage = Math.round((result / goal) * 100)

    const fontSize = {
      mobile: 10,
      tablet: 14,
      desktop: 16
    }

    const lineHeight = {
      mobile: 12,
      tablet: 18,
      desktop: 20
    }

    const chartWidth = {
      mobile: 140,
      tablet: contentSize === 'regular' ? 185 : 160,
      desktop: contentSize === 'regular' ? 256 : 210
    }

    const props: Omit<HalfDonutChartProps, 'value'> = {
      size: chartWidth[deviceType],
      backgroundColor: 'transparent',
      color: chartColor || '#006BFF',
      colors: [chartColor || '#006BFF', '#D9D9D9'],
      fontSize: `${fontSize[deviceType]}px`,
      lineHeight: `${lineHeight[deviceType]}px`,
      leftLabelColor: '#ffffff',
      rightLabelColor: '#303032',
      displayRightLabel: resultPercentage < 100,
      ...chartTemplate
    }

    return {
      props,
      resultPercentage
    }
  }, [contentSize, deviceType, chartColor, chartTemplate, result, goal])

  const formattedResult = `${prefix} ${formatNumberWithDots(result)}`
  const formattedGoal = `${prefix} ${formatNumberWithDots(goal)}`

  return (
    <>
      {info && (
        <InformationCard {...{ title, isOpen, close }}>
          <SafeHtmlContent html={info} />
        </InformationCard>
      )}
      <Card $contentSize={contentSize}>
        <CardHeader>
          <CardHeaderContent>
            <Text variant='content-06' as='h2'>
              {title}
            </Text>
            {subtitle && (
              <Text variant='content-03' as='p'>
                {subtitle}
              </Text>
            )}
          </CardHeaderContent>
          <Button
            variant='secondary'
            heightClass='small'
            disabled={!info}
            onClick={open}
            data-testid='info-button'
          >
            <Icon iconName='Information' icSize={'semiSmall'} />
          </Button>
        </CardHeader>
        <CardContentGrid>
          <CardContentRow>
            {imageUrl && <Image alt={title} src={imageUrl} />}
            <CardResultContent>
              <CardSubResultContent>
                <Text variant='content-03'>
                  {t('cockpit.result_goal.result')}
                </Text>
                <Text
                  variant='content-07'
                  as='h3'
                  style={{ color: chartColor || '#006BFF' }}
                >
                  {formattedResult}
                </Text>
              </CardSubResultContent>
              <CardSubResultContent>
                <Text variant='content-03'>
                  {t('cockpit.result_goal.goal')}
                </Text>
                <Text variant='content-07' as='h3'>
                  {formattedGoal}
                </Text>
              </CardSubResultContent>
            </CardResultContent>
          </CardContentRow>
          <ChartWrapper>
            <HalfDonutChart value={resultPercentage} {...props} />
          </ChartWrapper>
        </CardContentGrid>
      </Card>
    </>
  )
}

export default ResultGoalCardChartItem
